/**
 * 2026-10-14 (owner) · the formula a CALCULATION step works. Picked from the formula master; the
 * step keeps {code, version}, the batch freezes what the formula is at issue, and the tablet asks
 * for its inputs, has the server calculate, and saves the result (services/run/step_formula.py).
 * Only a Calculation step is offered one — the server refuses it on any other type.
 */
import React, { useEffect, useMemo, useState } from "react";
import Axios from "axios";
import { Select, Switch, Typography } from "antd";
import { Field } from "./Field";

const { Text } = Typography;

let cached = null;
function useFormulas() {
  const [rows, setRows] = useState(cached || []);
  useEffect(() => {
    if (cached) return undefined;
    let alive = true;
    Axios.get("/bp/calc/formulas/", { headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` } })
      .then((r) => {
        // The latest version of each code, and nothing retired or inactive.
        const latest = {};
        for (const f of r.data?.data || []) {
          if (["retired", "inactive"].includes(String(f.status || "").toLowerCase())) continue;
          if (!latest[f.code] || Number(f.version) > Number(latest[f.code].version)) latest[f.code] = f;
        }
        const list = Object.values(latest).sort((a, b) => String(a.name || a.code).localeCompare(String(b.name || b.code)));
        cached = list;
        if (alive) setRows(list);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return rows;
}

/** The variables an expression reads, for the formulas the master lists without their inputs. */
const variablesOf = (f) => (f?.inputs ? Object.keys(f.inputs)
  : Array.from(new Set(String(f?.expression || "").match(/[A-Za-z_][A-Za-z0-9_]*/g) || []))
    .filter((v) => !["round", "min", "max", "abs", "and", "or", "not", "if"].includes(v)));

/** The variables a dispense charge's formula takes from the material's BOM quantity (step_formula.MATERIAL_INPUTS). */
const MATERIAL_INPUTS = ["batch_qty", "material_qty", "qty", "quantity", "required_qty", "line_qty"];

export function FormulaField({ step, readOnly, onChange }) {
  // 2026-10-14 (owner) · a dispense charge may work a formula too: the material's quantity goes in,
  // and what it works out is the quantity the operator dispenses.
  const charge = ["MATERIAL_CHARGE", "DISPENSING"].includes(step?.step_type);
  const formulas = useFormulas();
  const code = step?.formula?.code || "";
  const chosen = useMemo(() => formulas.find((f) => f.code === code) || (step?.formula?.expression ? step.formula : null),
    [formulas, code, step?.formula]);
  const inputs = chosen ? (chosen.inputs && !Array.isArray(chosen.inputs) ? variablesOf(chosen)
    : (chosen.inputs || []).map((i) => i.name)) : [];
  return (
    <Field label={charge ? "Quantity formula" : "Formula"} required={!readOnly && !charge}
           hint={charge
             ? "Optional. Works out the quantity to dispense from this material's BOM quantity. On the tablet the operator enters the other inputs (e.g. assay), presses Calculate, saves, and then weighs against the result."
             : "What this calculation works out. On the tablet the operator enters its inputs and presses Calculate; the server computes and the result is saved."}>
      <Select size="small" showSearch allowClear optionFilterProp="label" disabled={readOnly}
              style={{ width: "100%" }} placeholder={readOnly ? "—" : charge ? "None — dispense the BOM quantity" : "Choose the formula — required"}
              status={!code && !readOnly && !charge ? "error" : undefined}
              value={code || undefined}
              onChange={(v) => onChange({ formula: v ? { code: v } : null })}
              options={formulas.map((f) => ({ value: f.code, label: `${f.name || f.code} · ${f.code}` }))} />
      {chosen ? (
        <div style={{ marginTop: 6 }}>
          <Text code style={{ fontSize: "calc(12.5px * var(--dz-fs, 1))" }}>{chosen.expression}</Text>
          {inputs.length ? (
            <Text type="secondary" style={{ display: "block", fontSize: "calc(12.5px * var(--dz-fs, 1))", marginTop: 4 }}>
              The operator enters: {inputs.filter((n) => !charge || !MATERIAL_INPUTS.includes(n)).map((n) => n.replace(/_/g, " ")).join(", ") || "nothing"}
              {charge && inputs.some((n) => MATERIAL_INPUTS.includes(n)) ? " · the material's quantity is taken from the BOM" : ""}
              {chosen.unit ? ` · result in ${String(chosen.unit).replace("{line_uom}", "the line's unit")}` : ""}
            </Text>
          ) : null}
        </div>
      ) : null}
    </Field>
  );
}

/**
 * 2026-10-19 (owner) · a calculation's "Verify against AR" switch (step_formula.VERIFY_KEY). On — the
 * operator selects each AR loaded with the BOM, sees what SAP gave for it, and verifies it through this
 * formula. Off — the formula is worked as entered: in a loop each pass's material quantity is filled
 * in, the operator enters the rest, calculates and submits; a charge that depends on this step
 * dispenses the result. On unless switched off.
 */
export function VerifyArField({ step, readOnly, onChange }) {
  const on = step?.verify_ar !== false;
  return (
    <Field label="Verify against AR"
           hint={on
             ? "On: the operator selects each AR of the material (from the BOM load), sees its SAP assay and quantity, and verifies it through the formula before submitting."
             : "Off: no AR verification — the formula is worked as entered (the loop's material quantity is filled in), and the result goes to the dispense charge that depends on this step."}>
      <Switch size="small" checked={on} disabled={readOnly} onChange={(v) => onChange({ verify_ar: v })}
              checkedChildren="On" unCheckedChildren="Off" />
    </Field>
  );
}

/**
 * 2026-10-14 (owner) · where a dispense charge's quantity comes from. A charge has no formula of its
 * own: it dispenses the bill-of-materials quantity, or the result saved on a CALCULATION step of the
 * same operation — chosen here, so the record says where the figure came from. Inside a dispensing
 * loop the calculation of the same pass feeds the charge, material by material.
 */
export function QuantityFromField({ step, operation, readOnly, onChange }) {
  // 2026-10-14 (owner) · every calculation step ABOVE this charge, in the order the operation runs —
  // two above it, two to choose from; one below it is not offered (the charge cannot wait on a
  // calculation that comes after it).
  // …and of the SAME loop: a charge inside a dispensing loop depends on a calculation of that loop
  // (worked for the same pass's material); a charge outside every loop, on one outside every loop.
  const calcs = useMemo(() => {
    const seen = [];                // [{node, loop}] above the charge, in run order
    let mine;                       // the loop the charge sits in ("" for none)
    const byRank = (nodes) => [...(nodes || [])].sort((a, b) => (a.position ?? a.rank ?? 0) - (b.position ?? b.rank ?? 0));
    const walk = (nodes, loop) => byRank(nodes).forEach((n) => {
      if (mine !== undefined) return;
      if (n.code === step?.code) { mine = loop; return; }
      if (n.step_type === "CALCULATION") seen.push({ node: n, loop });
      walk(n.children, n.kind === "LOOP" ? n.code : loop);
    });
    walk(operation?.children, "");
    return seen.filter((x) => x.loop === (mine ?? "")).map((x) => x.node);
  }, [operation, step?.code]);
  const q = step?.quantity_from || {};
  const value = q.kind === "STEP" ? q.step : undefined;
  const chosen = calcs.find((c) => c.code === value);
  // 2026-10-14 (owner) · "Dependent on": one choice — the calculation step whose result this charge
  // dispenses. Left empty, it dispenses the bill-of-materials quantity.
  return (
    <Field label="Dependent on"
           hint="The calculation step whose result this charge dispenses (e.g. the assay-corrected quantity). Leave empty to dispense the bill-of-materials quantity. In a dispensing loop each pass's calculation feeds that pass's charge.">
      <Select size="small" style={{ width: "100%" }} disabled={readOnly} allowClear showSearch optionFilterProp="label"
              placeholder={calcs.length ? "— none · the bill-of-materials quantity —" : "No calculation step above this one in the same loop"}
              value={value}
              status={value && !chosen ? "error" : undefined}
              onChange={(v) => onChange({ quantity_from: v ? { kind: "STEP", step: v } : null })}
              options={calcs.map((c) => ({
                value: c.code,
                label: `${c.code} · ${c.title || "Calculation"}${c.formula?.name ? ` — ${c.formula.name}` : " — no formula yet"}`,
                disabled: !c.formula?.code,
              }))} />
      {chosen?.formula?.expression ? (
        <Text type="secondary" style={{ display: "block", fontSize: "calc(12.5px * var(--dz-fs, 1))", marginTop: 4 }}>
          Dispenses the result of {chosen.code}: <Text code>{chosen.formula.expression}</Text>. Weighing opens once it is calculated and saved.
        </Text>
      ) : value && !chosen ? (
        <Text type="danger" style={{ display: "block", fontSize: "calc(12.5px * var(--dz-fs, 1))", marginTop: 4 }}>
          {value} is not a calculation step above this one any more — choose another or clear it.
        </Text>
      ) : null}
    </Field>
  );
}
